"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ReplenishmentClient = void 0;
const base_client_1 = require("@deliverr/base-client");
const commons_logs_1 = require("@deliverr/commons-logs");
const client_auth_1 = require("@deliverr/client-auth");
const core_auth_1 = require("@deliverr/core-auth");
const dtos_1 = require("./dtos");
class ReplenishmentClient extends base_client_1.ApiClient {
    constructor(config) {
        const REPLENISHMENT_SERVICE_URL = config.baseURL || process.env.REPLENISHMENT_SERVICE_URL;
        //Initially AuthEngine will be UNKNOWN,
        //as we want consumer of package to set the AuthEngine
        let apiClientConfig = {
            //AuthEngine.UNKNOWN will never be set in ApiClient
            authEngine: core_auth_1.AuthEngine.UNKNOWN,
        };
        if (!REPLENISHMENT_SERVICE_URL) {
            commons_logs_1.log.warn("REPLENISHMENT_SERVICE_URL environment variable is not set");
        }
        if (config.authEngine === core_auth_1.AuthEngine.COGNITO_INTERNAL) {
            const tokenHandler = new client_auth_1.OAuthClientTokenBuilder(config);
            apiClientConfig = {
                authEngine: core_auth_1.AuthEngine.COGNITO_INTERNAL,
                tokenRefreshFn: tokenHandler.getTokenFn(),
            };
        }
        else {
            apiClientConfig = {
                authEngine: config.authEngine,
            };
        }
        apiClientConfig = Object.assign(Object.assign({}, config), apiClientConfig);
        super(Object.assign(Object.assign({}, apiClientConfig), { baseURL: `${REPLENISHMENT_SERVICE_URL}` }));
    }
    async createReplenishmentOrder(createReplenishmentOrderRequest) {
        return await this.post(`/v1/orders/`, createReplenishmentOrderRequest);
    }
    async createChannelReplenishmentOrder(slsUuid, shipmentRequestModel) {
        return await this.post(`/v1/connector/channel/${slsUuid}/order`, shipmentRequestModel);
    }
    async createAndSubmitReplenishmentOrder(createSubmitReplenishmentOrderRequest) {
        return await this.post(`/v1/orders/create-submit`, createSubmitReplenishmentOrderRequest);
    }
    async getOrder(orderId, fetchOptions) {
        return await this.get(`/v1/orders/${orderId}`, fetchOptions);
    }
    async getOrders(filterOptions, fetchOptions) {
        let query = filterOptions !== null && filterOptions !== void 0 ? filterOptions : {};
        if (fetchOptions) {
            query = Object.assign(Object.assign({}, query), fetchOptions);
        }
        return await this.get(`/v1/orders`, query);
    }
    async searchOrders(searchRequest) {
        return await this.post("/v1/orders/search", searchRequest);
    }
    async updateOrder(orderId, replenishmentOrderUpdateRequest) {
        return await this.put(`/v1/orders/${orderId}`, replenishmentOrderUpdateRequest);
    }
    async patchOrder(orderId, replenishmentPatchOrderRequest) {
        return await this.patch(`/v1/orders/${orderId}`, replenishmentPatchOrderRequest);
    }
    async cancelOrder(orderId) {
        return await this.put(`/v1/orders/${orderId}/cancel`, {});
    }
    async archiveOrder(orderId) {
        return await this.put(`/v1/orders/${orderId}/archive`, {});
    }
    /**
     * @deprecated Use the new submitOrderV2 method instead.
     */
    async submitOrder(orderId, submitOrderRequest) {
        return await this.put(`/v1/orders/${orderId}/submit`, submitOrderRequest);
    }
    /**
     * This API allows submitting an order by calling sync API of wholesale service
     * @param orderId Order id
     * @param submitOrderRequest Submit order request
     */
    async submitOrderV2(orderId, submitOrderRequest) {
        return await this.put(`/v2/orders/${orderId}/submit`, submitOrderRequest);
    }
    async getShippingEstimates(orderId, shippingPartner, shippingType) {
        return await this.get(`/v1/estimate/shipping`, { shippingPartner, shippingType, orderId });
    }
    async getFreightEstimates(freightRequest) {
        return await this.post(`/v1/estimate/shipping/freight`, freightRequest);
    }
    async createOrUpdateSellerEnabledReport(sellerReportCreateOrUpdateRequest) {
        return await this.put(`/v1/reports`, sellerReportCreateOrUpdateRequest);
    }
    async enableReportsForSellerRelevantChannels(enableSellerReportsRequest) {
        return await this.post(`/v1/reports/enable`, enableSellerReportsRequest);
    }
    async requestShipmentAttachmentUploadUrl(shipmentId, uploadShipmentAttachmentRequest) {
        return await this.post(`/v1/shipments/${shipmentId}/attachments/upload-url`, uploadShipmentAttachmentRequest);
    }
    async deleteShipmentAttachment(shipmentId, attachmentType) {
        return await this.delete(`/v1/shipments/${shipmentId}/attachments/${attachmentType}`);
    }
    async markAttachmentAsUploaded(shipmentId, attachmentType) {
        return await this.put(`/v1/shipments/${shipmentId}/attachments/${attachmentType}/mark-uploaded`, {});
    }
    async getFBALocations() {
        return await this.get(`/v1/locations/${dtos_1.WarehouseLocationType.FBA}`);
    }
    async getOrderEstimates(orderId, orderEstimateRequest) {
        return await this.post(`/v1/orders/${orderId}/estimates`, orderEstimateRequest);
    }
    async getOrderEstimatesV2(orderId, orderEstimateRequest) {
        return await this.post(`/v2/orders/${orderId}/estimates`, orderEstimateRequest);
    }
    async getOrderCosts(orderId) {
        return await this.get(`/v1/orders/${orderId}/costs`);
    }
    async checkItemEligibility(distributionChannel, request) {
        return await this.post(`/v1/products/check-eligibility/${distributionChannel}`, request);
    }
    async getOrderTrackingHistory(orderId) {
        return await this.get(`/v1/orders/${orderId}/history`);
    }
    async patchShipment(shipmentId, patchShipmentRequest) {
        return await this.patch(`/v1/shipments/${shipmentId}`, patchShipmentRequest);
    }
    async getShipment(shipmentId) {
        return await this.get(`/v1/shipments/${shipmentId}`);
    }
    async getDownloadUrlsForShipmentAttachments(shipmentId) {
        return await this.get(`/v1/shipments/${shipmentId}/attachments/download-url`);
    }
    async getRestockReports(searchRestockReportRequest) {
        return await this.post(`/v1/reports/search/restock-report`, searchRestockReportRequest);
    }
    async requestSheetUploadUrl(uploadSheetAttachmentRequest) {
        return await this.post(`/v1/products/sheet/upload-url`, uploadSheetAttachmentRequest);
    }
    async bulkProductSpreadsheetUpload(bulkProductUploadRequest) {
        return await this.post(`/v1/products/bulk-upload`, bulkProductUploadRequest);
    }
    async invalidItemResolution(orderId, invalidOrderItemResolutionRequest) {
        return await this.put(`/v2/orders/${orderId}/invalid-item-resolution`, invalidOrderItemResolutionRequest);
    }
    async invalidItemResolutionBulk(orderId, invalidOrderItemResolutionBulkRequest) {
        return await this.put(`/v2/orders/${orderId}/invalid-item-resolution/bulk`, invalidOrderItemResolutionBulkRequest);
    }
    async getUnresolvedNonComplianceCountForSeller(sellerId) {
        return await this.get(`/v1/non-compliance/unresolved-count/${sellerId}`);
    }
    async getNonCompliancesByIds(ids, includeExternalNCDetails) {
        const commaSeparatedIds = ids.join(",");
        return await this.get(`/v1/non-compliance`, { ids: commaSeparatedIds, includeExternalNCDetails });
    }
    async getNonCompliancesByShipmentIds(shipmentIds, includeExternalNCDetails) {
        const commaSeparatedShipmentIds = shipmentIds.join(",");
        return await this.get(`/v1/non-compliance/shipments`, {
            shipmentIds: commaSeparatedShipmentIds,
            includeExternalNCDetails,
        });
    }
    async resolveNonComplianceById(id) {
        return await this.put(`/v1/non-compliance/${id}/resolve`, {});
    }
    async validateNonCompliance(validateNonComplianceRequest) {
        return await this.post(`/v1/non-compliance/validate`, validateNonComplianceRequest);
    }
    async fetchAvailableWarehouseInventory(dskuQuantities, fulfillStrategy = dtos_1.FulfillStrategy.DEFAULT) {
        return await this.post(`/v1/inventory/available/warehouse`, dskuQuantities, { fulfillStrategy });
    }
    async createCarrierAccount(createCarrierAccountRequest) {
        return await this.post(`/v1/carrier-accounts/`, createCarrierAccountRequest);
    }
    async getCarrierAccountsBySellerId(sellerId) {
        return await this.get(`/v1/carrier-accounts/${sellerId}`);
    }
    async updateCarrierAccountById(id, updateCarrierAccountRequest) {
        return await this.put(`/v1/carrier-accounts/${id}`, updateCarrierAccountRequest);
    }
    async getPackingOptions(orderId) {
        return await this.get(`/v1/amazon/${orderId}/packing-options`);
    }
    async getPlacementOptions(orderId) {
        return await this.get(`/v1/amazon/${orderId}/placement-options`);
    }
    async getTransportationOptions(orderId, placementOptionId, shippingPartner) {
        return await this.get(`/v1/amazon/${orderId}/transportation-options`, {
            placementOptionId,
            shippingPartner,
        });
    }
    async confirmPackingOption(orderId, packingOptionId) {
        return await this.put(`/v1/amazon/${orderId}/packing-options/${packingOptionId}/confirm`, {});
    }
    async retryOperation(operationId, orderId) {
        return await this.put(`/v1/operations/${operationId}/retry`, { orderId });
    }
    async fetchOperations(fetchOperationsRequest) {
        return await this.post(`/v1/operations`, fetchOperationsRequest);
    }
    async confirmPlacementOption(orderId, placementOptionId) {
        return await this.put(`/v1/amazon/${orderId}/placement-options/${placementOptionId}/confirm`, {});
    }
    async getFbaOrderEstimates(orderId, orderEstimateRequest) {
        return await this.post(`/v1/amazon/${orderId}/estimates`, orderEstimateRequest);
    }
    async confirmFBAOrder(orderId, fbaConfirmOrderRequest) {
        return await this.put(`/v1/amazon/${orderId}/order`, fbaConfirmOrderRequest);
    }
    async setItemPrepDetails(sellerId, itemPrepDetails) {
        return await this.post(`/v1/amazon/${sellerId}/item-prep`, itemPrepDetails);
    }
    async listItemPrepDetails(sellerId, listItemPrepDetailsRequest) {
        return await this.post(`/v1/amazon/${sellerId}/list-item-prep`, listItemPrepDetailsRequest);
    }
    async updateBoxMappings(shipmentId, mappings) {
        return await this.post(`/v1/ops/shipments/${shipmentId}/box-mappings`, { mappings });
    }
    async getUnifiedAvailableInventory(dskus, poolName) {
        return await this.post(`/v1/inventory/unified`, {
            dskus,
        }, { poolName });
    }
    /**
     * This is the new unified API which can be used to create and submit orders.
     * @param createOrderRequest - The request payload for creating an order.
     * @returns A promise that resolves to the created order response.
     */
    async createOrder(createOrderRequest) {
        return await this.post(`/v2/orders`, createOrderRequest);
    }
    /**
     * This API is used to update an existing replenishment order.
     * @param orderId - The ID of the order to update.
     * @param updateOrderRequest - The request payload for updating the order.
     * @returns A promise that resolves to the updated order response.
     */
    async updateOrderV2(orderId, updateOrderRequest) {
        return await this.put(`/v2/orders/${orderId}`, updateOrderRequest);
    }
    /**
     * This API is used to get an order by its externalId.
     * @param externalId - The externalId of the order.
     * @param sellerId - The sellerId of the seller requesting the order.
     * @param source - The source of the order.
     * @param fetchOptions - The fetch options for the order.
     * @returns A promise that resolves to the order response.
     */
    async getOrderByExternalId(externalId, sellerId, source, fetchOptions) {
        const query = Object.assign({ source, sellerId }, (fetchOptions !== null && fetchOptions !== void 0 ? fetchOptions : {}));
        return await this.get(`/v2/orders/externalId/${externalId}`, query);
    }
    async resolveBulkProductUpload(bulkProductResolutionRequest) {
        return await this.post(`/v1/products/resolve-bulk-upload`, bulkProductResolutionRequest);
    }
    /**
     * This API is used to confirm an order.
     * @param orderId - The id of the order.
     * @param confirmOrderRequest - The request payload for confirming an order.
     */
    async confirmOrder(orderId, confirmOrderRequest) {
        return await this.put(`/v2/orders/${orderId}/confirm`, confirmOrderRequest);
    }
}
exports.ReplenishmentClient = ReplenishmentClient;
//# sourceMappingURL=ReplenishmentClient.js.map